import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    Avatar,
    Rating,
    Chip
} from '@mui/material';
import { Eye, User, X } from 'lucide-react';
import { getAllReviews, getReviewById } from '../../Services/ReviewManagement/ReviewApis';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';

const Reviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Detail modal states
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [reviewDetail, setReviewDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchReviews = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllReviews();
            if (response && response.success) {
                setReviews(response.data);
            } else {
                setError("Failed to load reviews.");
            }
        } catch (err) {
            setError("Failed to fetch reviews. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const handleViewDetail = async (id) => {
        setDetailModalOpen(true);
        setLoadingDetail(true);
        try {
            const response = await getReviewById(id);
            if (response && response.success) {
                setReviewDetail(response.data);
            } else {
                setError("Failed to load review details.");
            }
        } catch (err) {
            setError("Failed to fetch review details.");
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleCloseDetailModal = () => {
        setDetailModalOpen(false);
        setReviewDetail(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Helper to group photos by type
    const groupPhotosByType = (photos) => {
        if (!photos) return {};
        return photos.reduce((acc, photo) => {
            const type = photo.photoType || 'other';
            if (!acc[type]) acc[type] = [];
            acc[type].push(photo);
            return acc;
        }, {});
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500 }}>
                        Reviews
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '6px' }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "6px", border: "1px solid #E2E8F0", overflow: "hidden", bgcolor: '#FFFFFF' }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ width: '100%', minWidth: 1100, tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>S.No</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>User Name</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Retreat Title</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Overall Rating</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Show As</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Date</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : reviews.length > 0 ? (
                                    reviews.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell align="left" sx={{ color: '#64748B', py: 1.25, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{idx + 1}</TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#1E293B', fontWeight: 500, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {item.userName || '—'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 260 }}>
                                                <Typography sx={{ color: '#475569', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {item.retreatTitle || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Rating value={parseFloat(item.overallRating)} readOnly precision={0.1} size="small" />
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B' }}>
                                                        {item.overallRating}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#475569', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {item.showAsName || 'Anonymous'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createDate)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || item.userName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt || item.createDate)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="view" mode="disable">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#64748B', p: 0.5, '&:hover': { color: '#1E293B' } }}
                                                                onClick={() => handleViewDetail(item.id)}
                                                            >
                                                                <Eye size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            No reviews found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* REVIEW DETAILS MODAL */}
                <Dialog
                    open={detailModalOpen}
                    onClose={handleCloseDetailModal}
                    maxWidth="md"
                    fullWidth
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF'
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                        <Box>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                                Review Details
                            </Typography>
                            {reviewDetail && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.25 }}>
                                    Retreat: <strong style={{ color: '#1E293B' }}>{reviewDetail.retreatTitle || '—'}</strong>
                                </Typography>
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseDetailModal}
                            sx={{ color: '#64748B', p: 0.5 }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        {loadingDetail ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : reviewDetail ? (
                            <Stack spacing={3}>
                                {/* Profile & Overall Score Banner */}
                                <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar
                                            src={reviewDetail.reviewerImage}
                                            sx={{ width: 52, height: 52, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', color: '#64748B' }}
                                        >
                                            <User size={26} />
                                        </Avatar>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                                                {reviewDetail.title || 'Review Feedback'}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.25 }}>
                                                By: <strong style={{ color: '#1E293B' }}>{reviewDetail.showAsName || 'Anonymous'}</strong>
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mt: 0.25 }}>
                                                Submitted on: {formatDate(reviewDetail.createdAt)}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '6px', px: 2, py: 1.25 }}>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Overall Rating
                                        </Typography>
                                        <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#1E293B', lineHeight: 1.2 }}>
                                            {reviewDetail.overallRating}
                                        </Typography>
                                        <Rating value={parseFloat(reviewDetail.overallRating)} readOnly precision={0.1} size="small" sx={{ mt: 0.25 }} />
                                    </Box>
                                </Box>

                                {/* Rating Breakdown */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                                        Rating Breakdown
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {[
                                            { label: 'Program Quality', value: reviewDetail.programQuality },
                                            { label: 'Location', value: reviewDetail.location },
                                            { label: 'Staff Behavior', value: reviewDetail.staffBehavior },
                                            { label: 'Food Quality', value: reviewDetail.foodQuality },
                                            { label: 'Accommodation', value: reviewDetail.accommodation },
                                            { label: 'Description Accuracy', value: reviewDetail.descriptionAccuracy },
                                            { label: 'Value for Money', value: reviewDetail.valueForMoney },
                                            { label: 'Communication', value: reviewDetail.communication }
                                        ].map((rating) => (
                                            <Grid item xs={12} sm={6} md={3} key={rating.label}>
                                                <Box sx={{ p: 1.5, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                                                        {rating.label}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                        <Rating value={parseFloat(rating.value || 0)} readOnly size="small" precision={0.1} />
                                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B' }}>
                                                            {rating.value || '0'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>

                                {/* Questionnaire Responses */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                                        Responses & Answers
                                    </Typography>
                                    {reviewDetail.answers && reviewDetail.answers.length > 0 ? (
                                        <Stack spacing={1.5}>
                                            {reviewDetail.answers.map((ans) => (
                                                <Box key={ans.id} sx={{ p: 1.75, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', mb: 0.5 }}>
                                                        {ans.question?.question || 'Question'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.5 }}>
                                                        "{ans.answer}"
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Box sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>No questionnaire responses provided.</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Photos Section */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                                        Uploaded Photos
                                    </Typography>
                                    {reviewDetail.photos && reviewDetail.photos.length > 0 ? (
                                        <Box>
                                            {Object.entries(groupPhotosByType(reviewDetail.photos)).map(([type, photos]) => (
                                                <Box key={type} sx={{ mb: 2 }}>
                                                    <Chip
                                                        label={type.toUpperCase()}
                                                        size="small"
                                                        sx={{ mb: 1.5, bgcolor: '#F1F5F9', color: '#475569', fontWeight: 500, fontSize: '0.7rem', height: 22 }}
                                                    />
                                                    <Grid container spacing={1.5}>
                                                        {photos.map((photo) => (
                                                            <Grid item xs={6} sm={4} md={3} key={photo.id}>
                                                                <Card sx={{ border: '1px solid #E2E8F0', borderRadius: '6px', position: 'relative', overflow: 'hidden' }} elevation={0}>
                                                                    <Box
                                                                        component="img"
                                                                        src={photo.imageUrl}
                                                                        alt={type}
                                                                        sx={{
                                                                            width: '100%',
                                                                            height: 120,
                                                                            objectFit: 'cover',
                                                                            cursor: 'pointer',
                                                                            display: 'block'
                                                                        }}
                                                                        onClick={() => window.open(photo.imageUrl, '_blank')}
                                                                    />
                                                                    {/* Delete photo option commented out per request - API logic preserved */}
                                                                    {/* <PermissionWrapper permission="delete" mode="disable">
                                                                        <Tooltip title="Delete Photo">
                                                                            <IconButton
                                                                                size="small"
                                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleDeletePhoto(photo.id);
                                                                                }}
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: 6,
                                                                                    right: 6,
                                                                                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                                                    border: '1px solid #E2E8F0',
                                                                                    color: '#EF4444',
                                                                                    p: 0.5,
                                                                                    '&:hover': { bgcolor: '#FEE2E2' }
                                                                                }}
                                                                            >
                                                                                <Trash2 size={13} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </PermissionWrapper> */}
                                                                </Card>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>No photos uploaded for this review.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        ) : (
                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>No details found.</Typography>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleCloseDetailModal}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 3,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default Reviews;
